import React from 'react';
import { resolveLoaderBranding, type ResolveLoaderBrandingOptions } from '../utils/loaderBranding';

export interface BrandedLoaderProps extends ResolveLoaderBrandingOptions {
  /** Short status line under the logo. */
  label?: string;
  /**
   * `fullscreen` — black page (chunk / tour bootstrap).
   * `overlay` — centred over the panorama (chrome can stay visible).
   */
  variant?: 'fullscreen' | 'overlay';
  className?: string;
}

/**
 * Brand-aware loading indicator: logo mark inside a dual-tone spinning ring.
 * Used for the initial tour bootstrap, Suspense fallbacks, and the panorama
 * overlay while a scene loads.
 */
const BrandedLoader: React.FC<BrandedLoaderProps> = ({
  label = 'Loading…',
  variant = 'fullscreen',
  className = '',
  ...brandOpts
}) => {
  const brand = resolveLoaderBranding(brandOpts);
  const isOverlay = variant === 'overlay';

  return (
    <div
      className={
        isOverlay
          ? `absolute inset-0 z-10 flex items-center justify-center pointer-events-none ${className}`
          : `flex min-h-screen w-full items-center justify-center bg-black ${className}`
      }
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className="relative flex items-center justify-center"
          style={{ width: isOverlay ? 132 : 160, height: isOverlay ? 132 : 160 }}
        >
          {/* Soft brand glow */}
          <div
            className="absolute inset-0 rounded-full blur-2xl opacity-40"
            style={{
              background: `radial-gradient(circle, ${brand.accent}55 0%, ${brand.accentSecondary}33 45%, transparent 70%)`,
            }}
          />
          {/* Spinning dual-tone ring */}
          <div
            className="absolute inset-0 rounded-full animate-spin"
            style={{
              animationDuration: '1.15s',
              background: `conic-gradient(from 0deg, ${brand.accent}, ${brand.accentSecondary}, transparent 65%)`,
              WebkitMask:
                'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3px))',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3px))',
            }}
          />
          {/* Disc + logo */}
          <div
            className="relative z-10 flex items-center justify-center rounded-full border border-white/10 bg-black/80 shadow-2xl"
            style={{
              width: isOverlay ? 108 : 132,
              height: isOverlay ? 108 : 132,
            }}
          >
            <img
              src={isOverlay ? brand.markUrl : brand.logoUrl}
              alt={brand.name}
              className="object-contain"
              style={{
                width: isOverlay ? 64 : 100,
                height: isOverlay ? 64 : 100,
              }}
              draggable={false}
            />
          </div>
        </div>
        {label && (
          <p
            className="text-sm font-medium tracking-wide animate-pulse"
            style={{ color: brand.isWorlds ? '#a5f3fc' : '#94a3b8' }}
          >
            {label}
          </p>
        )}
      </div>
    </div>
  );
};

export default BrandedLoader;
